(ns frontend.views.roadmap
  (:require
   [common.css           :as css]
   [common.quantity      :as qty]
   [common.util          :as u]
   [opengb.color         :as color]
   [opengb.i18n          :refer [Tr]]
   [re-frame.core        :as rf]
   [reagent.dom.client   :as rg-dom-client]
   [shadow.graft         :as graft]
   [tailwind-hiccup.core :refer [tw]]))

;; Currency Wrapper

(defn MaybeCurrency
  [q]
  (if (= "CAD" (qty/get-unit q))
    (if (< (qty/get-magnitude q) 0)
      [:span "($" [qty/Mag (qty/qty-fmap #(* -1 %) q)] ")"]
      [:span "$" [qty/Mag q]])
    [:span [qty/Mag q] [qty/Unit q]]))

;; Helpers

(defn should-enable-click?
  "Returns true if a step should be clickable."
  [{:keys [:rvm/selected
           :rvm/step-key]
    :as _step}]
  (and (not= step-key :roadmap/step-baseline)
       (not= selected :selected)))

;; Events

(rf/reg-event-fx :frontend.views.roadmap/set-selected-step
                 (fn [{:keys [db] :as _cofx} [_ step-key]]
                   {:db (assoc db :frontend.views.roadmap/selected-step step-key)}))

;; Subscriptions

(rf/reg-sub :frontend.views.roadmap/selected-step
            (fn [db _] (:frontend.views.roadmap/selected-step db)))

;; Step Details

(defn- detail:scroll-to-details
  "Scrolls page to bring step details into view."
  [id]
  (let [selector  (str "#" id)
        container (.querySelector js/document selector)]
    (.scrollIntoView container (clj->js {:behavior "smooth"}))))

(defn detail:TableHeader
  [{:keys [:rvm/step-color] :as _step}]
  [:thead
   [:tr (tw [:leading-5])
    [:td (tw [:w-2])]
    [:td]
    [:th (tw [:px-2 :py-2 :font-medium]
             {:style {:background (color/single step-color 2)}})
     [Tr :frontend.views.roadmap/this-step]]
    [:th (tw [:pl-2 :py-2 :font-medium])
     [Tr :frontend.views.roadmap/this-plus-previous]]
    [:td (tw [:w-2])]]])

(def color-negative (color/single ::color/negative 1))
(def color-positive (color/single ::color/positive 1))

(defn detail:TableValue
  [{:keys [border-color
           color-cost-savings?
           explanation
           qty
           step-color
           value-tw]}]
  (let [text-color (when color-cost-savings?
                     (if (< (qty/get-magnitude qty) 0) color-negative color-positive))]
    [:td (tw [:text-right :px-2] value-tw
             {:style {:color text-color
                      :background (color/single step-color 2)
                      :border-bottom (when (nil? explanation)
                                       (str "solid 1px" border-color))}})
     [MaybeCurrency qty]]))

(defn detail:CostSavings
  []
  [:span
   [:span (tw [] {:style {:color color-negative}}) [Tr :frontend.views.roadmap/cost]]
   "/"
   [:span (tw [] {:style {:color color-positive}}) [Tr :frontend.views.roadmap/savings]]])

(defn detail:TableRow
  [{:keys [:rvm/step-color] :as step}
   {:keys [border-color
           color-cost-savings?
           show-zeroes?
           explanation-key
           row-tw
           title-key
           value-key
           value-tw]}]
  (let [current-value        (get-in step [:rvm/individual-values value-key])
        cumulative-value     (get-in step [:rvm/cumulative-values value-key])
        manual-border-color? border-color
        border-color         (or border-color
                                 (color/single ::color/neutral 8))
        border-bottom             (when (nil? explanation-key)
                                    (str "solid 1px" border-color))]
    (when
     (or show-zeroes?
         (and (not= (qty/get-magnitude current-value) 0)
              (not= (qty/get-magnitude cumulative-value) 0)))
      [:<>
       [:tr (tw [:group] row-tw)
        [:td (tw [:w-2]
                 {:style {:border-bottom border-bottom}})]
        [:td (tw [:leading-5 :align-top :pt-1 :pr-8]
                 {:style {:border-bottom border-bottom}})
         [Tr title-key {:html? true}]
         (when color-cost-savings? (detail:CostSavings))]
        [detail:TableValue {:border-color (if manual-border-color?
                                            border-color
                                            (color/single ::color/neutral 6))
                            :color-cost-savings? color-cost-savings?
                            :explanation explanation-key
                            :qty current-value
                            :step-color step-color
                            :value-tw value-tw}]
        [detail:TableValue {:border-color border-color
                            :color-cost-savings? color-cost-savings?
                            :explanation explanation-key
                            :qty cumulative-value
                            :step-color nil
                            :value-tw value-tw}]

        [:td (tw [:w-2]
                 {:style {:border-bottom border-bottom}})]]
       (when explanation-key
         [:tr
          [:td {:style {:border-bottom border-bottom}}]
          [:td (tw [:text-xs :italic :pb-2])
           [:div {:style {:max-width "350px"
                          :border-bottom border-bottom}}
            [Tr explanation-key]]]
          [:td {:style {:background (color/single step-color 2)
                        :border-bottom (str "solid 1px " (color/single ::color/neutral 6))}}]

          [:td]
          [:td]])])))

(defn detail:TableFooter
  [{:keys [:rvm/step-color] :as step}]
  (let [current-value    (get-in step [:rvm/individual-values :rvm/l-annual-carrying])
        cumulative-value (get-in step [:rvm/cumulative-values :rvm/l-annual-carrying])]
    (when
     (and (not= (qty/get-magnitude current-value) 0)
          (not= (qty/get-magnitude cumulative-value) 0))
      [:<>
       [:tr (tw [:group :font-medium :bg-neutral-8])
        [:td (tw [:w-2])]
        [:td (tw [:leading-5])
         [Tr :frontend.views.roadmap/annual-carrying {:html? true}]
         (detail:CostSavings)]
        [detail:TableValue {:color-cost-savings? true
                            :explanation :frontend.views.roadmap/annual-carrying-explanation
                            :qty current-value
                            :step-color step-color}]
        [detail:TableValue {:color-cost-savings? true
                            :explanation :frontend.views.roadmap/annual-carrying-explanation
                            :qty cumulative-value
                            :step-color nil}]

        [:td (tw [:w-2])]]
       [:tr (tw [:bg-neutral-8])
        [:td]
        [:td (tw [:text-xs :italic :pb-2])
         [:div {:style {:max-width "350px"}} [Tr :frontend.views.roadmap/annual-carrying-explanation]]]
        [:td {:style {:background (color/single step-color 2)}}]
        [:td]
        [:td]]])))

(defn detail:Table
  [step]
  [:div (tw [:-mx-2 :flex :flex-col :gap-y-3])
   [:table (tw [:w-full :xl:w-auto :md:text-lg])
    [detail:TableHeader step]
    [:tbody
     [detail:TableRow step {:title-key :frontend.views.roadmap/project-cost
                            :value-key :rvm/b-project-cost}]
     [detail:TableRow step {:title-key :frontend.views.roadmap/conventional-replacement
                            :value-key :rvm/c-status-quo-cost}]
     [detail:TableRow step {:title-key :frontend.views.roadmap/greener-homes-grant
                            :border-color (color/single ::color/neutral 2)
                            :value-key :rvm/d-rebate-value
                            :show-zeroes? true ;; FLEX-169: need to show rebate even when $0.
                            }]
     [detail:TableRow step {:title-key :frontend.views.roadmap/net-project
                            :value-key :rvm/h-net-project-cost
                            :value-tw ^:tw [:pb-5]
                            :row-tw ^:tw [:font-normal]
                            :color-cost-savings? true}]
     [detail:TableRow step {:title-key :frontend.views.roadmap/annual-energy
                            :value-key :rvm/g-energy-cost-savings
                            :value-tw ^:tw [:pb-3]
                            :row-tw ^:tw [:font-medium]
                            :color-cost-savings? true}]
     [detail:TableRow step {:title-key :frontend.views.roadmap/ten-year-cost-recovery
                            :row-tw ^:tw [:font-medium :border-b-0]
                            :value-key :rvm/k-payback-factor
                            :explanation-key :frontend.views.roadmap/ten-year-explanation}]
     [detail:TableFooter step]]]])

(defn faq:Entry
  [{:keys [question-id idx step-key]}]
  [:details (tw [:py-2]
                {:open (= idx 0)})
   [:summary (tw [:cursor-pointer])
    [:span (tw [:font-medium :text-lg])
     [Tr [step-key :question question-id]]]]
   [:div (tw [:prose :prose-open-theme])
    [Tr [step-key :answer question-id] {:html? true}]]])

;; Ordered FAQ topic slugs shown for each roadmap action on this V1 ROADMAP.
;; The V1 roadmap is the legacy saskatoon roadmap which is not tenant-aware.
;; The same topics are shown for all tenants on the v1 roadmap (which will eventually be disposed of in favour of the v2 roadmap).
(def step-key->faq-topics
  {:roadmap/step-air-tightness   [:what-is-air-leakage
                                  :why-should-i-air-seal-my-home
                                  :what-does-this-entail]
   :roadmap/step-ceiling         [:what-is-attic-insulation
                                  :why-should-i-add-attic-insulation
                                  :what-does-this-entail]
   :roadmap/step-windows         [:what-are-windows
                                  :why-should-i-replace-my-windows
                                  :what-does-this-entail]
   :roadmap/step-wall-insulation [:what-is-wall-insulation
                                  :why-should-i-add-insulation-to-my-walls
                                  :what-does-this-entail]
   :roadmap/step-hot-water       [:what-is-a-hot-water-heater
                                  :why-should-i-install-a-heat-pump-hot-water-heater
                                  :what-does-it-entail]
   :roadmap/step-solar           [:what-are-solar-panels
                                  :why-should-i-install-them
                                  :what-does-it-entail
                                  :is-my-home-suitable-for-solar-panels]})

(defn detail:Questions
  [step-key]
  (let [questions (step-key->faq-topics step-key)]
    [:div
     [:div (tw [:flex :flex-col :max-w-lg
                :divide-y :divide-neutral-6
                :print:hidden])
      (map-indexed
       (fn [idx question-id]
         ^{:key question-id}
         [faq:Entry {:question-id question-id
                         :step-key    step-key
                         :idx         idx}])
       questions)]]))

(defn detail:Equipment:Entry
  [{:keys [question-key answer-key open?]}]
  [:details (tw [:py-2] (when open? {:open true}))
   [:summary (tw [:cursor-pointer])
    [:span (tw [:font-medium :text-lg])
     [Tr question-key]]]
   [:div (tw [:prose :prose-open-theme])
    [Tr answer-key {:html? true}]]])

(defn detail:Equipment
  [step]
  (let [scenario-key (get-in step [:rvm/cumulative-values :rvm/m-equipment-scenario])]
    [:div (tw [:flex :flex-col :max-w-lg
               :divide-y :divide-neutral-6
               :print:hidden])
     [detail:Equipment:Entry
      {:question-key [:frontend.views.roadmap/equipment :question :what-is-a-heat-pump]
       :answer-key   [:frontend.views.roadmap/equipment :answer :what-is-a-heat-pump]
       :open?        true}]
     (when scenario-key
       [detail:Equipment:Entry
        {:question-key [:frontend.views.roadmap/equipment :question :why-should-i-install-a-heat-pump]
         :answer-key   [:frontend.views.roadmap/equipment :answer :why-should-i-install-a-heat-pump scenario-key]}])]))

(defn detail:ECM
  [{:keys [:rvm/step-key] :as step}]
  [:div (tw [:flex :flex-col :gap-y-5
             :xl:flex-row :xl:gap-x-10])
   (if (= step-key :roadmap/step-equipment)
     [detail:Equipment step]
     [detail:Questions step-key])
   [:div (tw [:px-2 :grow :space-y-5
              :xl:pl-0 :xl:max-w-3xl])
    [detail:Table step]
    [:div (tw [:print:hidden :prose :prose-open-theme])
     [:p (tw [:text-xs])
      [:sup (tw [:font-bold]) "*"]
      " " [Tr :frontend.views.roadmap/greener-homes-suspended]]]]])

(defn detail:Baseline
  [_step]
  [:div {:style {:width "1271.87px"
                 :height "700px"}}
   [:div (tw [:prose :prose-open-theme])
    [:p [Tr :frontend.views.roadmap/baseline-body]]]])

(defn detail:NavButton
  "Button that changes selected step."
  [color-key step-key label-key]
  (if step-key
    [:button (tw [:px-2 :rounded
                  :text-neutral-12 :hover:text-neutral-2]
                 {:style {:background (color/single color-key 1)}
                  :on-click #(rf/dispatch [:frontend.views.roadmap/set-selected-step
                                           step-key])})
     [Tr label-key]]
    [:div]))

(defn detail:Nav
  "Provides buttons to navigate between roadmap steps."
  [{:keys [:rvm/step-color
           :rvm/step-next
           :rvm/step-previous] :as _step}]
  [:div (tw [:flex :justify-between])
   [detail:NavButton step-color step-previous :frontend.views.roadmap/previous-step]
   [detail:NavButton step-color step-next :frontend.views.roadmap/next-step]])

(defn detail:Step
  [{:keys [:rvm/step-color
            :rvm/step-key
            :rvm/step-title] :as step}]
  [:div (tw [:border-2 :border-neutral-10 :rounded-xl :relative :z-20])
   [:div (tw [:flex :flex-col :gap-y-5 :p-3
              :border-8 :rounded-xl]
             {:style {:border-color (color/single step-color 2)}})
    [:h2 (tw css/h2) step-title]
    (if (= step-key :roadmap/step-baseline)
      [detail:Baseline step]
      [detail:ECM step])
    [detail:Nav step]]])

(defn detail:Wrapper
  "Placeholder view for displaying additional information about a selected ECM."
  [steps id]
  (let [steps-table        (into {} (map (juxt :rvm/step-key identity)) steps)
        *selected-step-key (rf/subscribe [:frontend.views.roadmap/selected-step])
        selected-step      (get steps-table @*selected-step-key)]
    (when selected-step
      [:div
       [:div (tw [:h-4 :-mt-10] {:id id})]
       [detail:Step selected-step]])))

(defn print:ECM
  [step]
  [:div (tw [:flex :flex-col :gap-y-5
             :xl:flex-row :xl:gap-x-10])
   [:div (tw [:px-2 :xl:pl-0 :grow])
    [detail:Table step]]])

(defn print:Step
  [{:keys [:rvm/step-color
           :rvm/step-key
           :rvm/step-title] :as step}]
  (when-not (= step-key :roadmap/step-baseline)
    [:div (tw [:border-2 :border-neutral-10 :rounded-xl :relative :z-20])
     [:div (tw [:flex :flex-col :gap-y-5 :p-3
                :border-8 :border-yellow-2 :rounded-xl]
               {:style {:border-color (color/single step-color 2)}})
      [:header (tw [:flex :justify-between :items-center])
       [:h3 (tw css/h3) step-title]]
      [print:ECM step]]]))

(defn print:Wrapper
  "View for displaying printable information about each ECM."
  [steps]
  [:div {:id "print-ecm-steps"}
   (for [step (rest steps)] ; don't show baseline step in print
     ^{:key (:rvm/step-key step)}
     [:div
      (tw
       [:break-inside-avoid-page]
       {:id (:rvm/step-key step)})
      [print:Step step]])])

;; Wide Roadmap

(defn wide:step->hex-color
  [{:keys [:rvm/step-color :rvm/selected]}]
  (color/single step-color (case selected
                             :default    2
                             :unselected 3
                             :selected   2)))

(defn wide:top-row-height->full-height
  [height]
  (str (+ height 10.5) "rem"))

(defn wide:ClickView
  [top-row-height step-key]
  [:div (tw [:relative :cursor-pointer
             :-m-2 :z-10 :opacity-0 :rounded-xl
             :hover:bg-neutral-2 :hover:opacity-20]
            {:style {:height (wide:top-row-height->full-height top-row-height)}
             :on-click (fn [_]
                         (rf/dispatch [:frontend.views.roadmap/set-selected-step
                                       step-key])
                         (js/setTimeout (partial detail:scroll-to-details "ecm-detail-wide") 200))})])

(defn wide:calculate-height
  [step]
  (let [min-header-height  4.25 ;; fits 3 lines of text
        bottom-rows-height 10.5 ;; 4 rows and footer
        base-height        (+ bottom-rows-height
                              min-header-height)
        {:keys [:rvm/total-energy-max
                :rvm/total-energy-min]} step
        total-energy-step (-> step
                              :rvm/cumulative-values
                              :rvm/a-total-energy-use
                              qty/get-magnitude)]
    (-> total-energy-step
        (/ total-energy-max)
        (* base-height)
        (/ (/ total-energy-min
              total-energy-max))
        (- bottom-rows-height))))

(defn wide:TopRow
  [steps]
  [:tr
   [:td]
   (for [{:keys [:rvm/cumulative-values
                 :rvm/step-key
                 :rvm/step-title]
          :as step} steps]
     (let [{:keys [:rvm/a-total-energy-use]} cumulative-values
           enable-click?  (should-enable-click? step)
           step-hex-color (wide:step->hex-color step)
           height         (wide:calculate-height step)
           top-offset     (if enable-click?
                            (str "-" (wide:top-row-height->full-height height))
                            "0")]
       ^{:key step-key}
       [:th (tw [:align-bottom :font-light])
        [:div (tw [:text-center :p-2 :rounded-t-xl :w-28]
                  {:style {:background step-hex-color
                           :height (str height "rem")}})
         (when enable-click?
           [wide:ClickView height step-key])
         [:div (tw [:relative (when enable-click? :mt-4)]
                   {:style {:top top-offset}})
          [:div (tw [:font-medium :leading-4])
           (if (= step-key :roadmap/step-baseline)
             step-title
             (str "+ " step-title))]
          [:div
           [qty/Mag a-total-energy-use]
           [qty/Unit a-total-energy-use]]]]]))])

(defn wide:ValueRow
  [steps row-title-key value-keypath]
  [:tr
   [:th (tw [:text-right :whitespace-nowrap :font-medium])
    [Tr row-title-key]]
   (for [{:keys [:rvm/step-key] :as step} steps]
     (let [step-hex-color (wide:step->hex-color step)]
       ^{:key step-key}
       [:td
        [:div (tw [:py-1 :px-3 :text-right :w-28
                   :overflow-x-clip]
                  {:style {:background step-hex-color}})
         (let [value (get-in step value-keypath)]
           (if value
             [MaybeCurrency value]
             [:div {:style {:height "31.5px"}}]))]]))])

(defn wide:BottomRow
  [steps]
  [:tr
   [:td]
   (for [{:keys [:rvm/step-key] :as step} steps]
     (let [step-hex-color (wide:step->hex-color step)]
       ^{:key step-key}
       [:td
        [:div (tw [:h-4 :rounded-b-xl]
                  {:style {:background step-hex-color}})]]))])

(defn wide:SelectRow
  [steps]
  [:tr
   [:td]
   (for [{:keys [:rvm/selected] :as step} steps]
     (let [step-hex-color (wide:step->hex-color step)]
       ^{:key (:rvm/step-key step)}
       [:td
        (when (= selected :selected)
          [:div (tw [:relative :-top-4 :h-6
                     :rounded-b-xl]
                    {:style {:background step-hex-color}})])]))])

(defn wide:Roadmap
  "Displays ECM Pathway as a vertical bar chart with additional values in an
  integrate table."
  [steps]
  [:div (tw [:pb-11 :print:hidden])
   [:div (tw [:hidden :md:block
              "max-w-[100vw]" #_:md:max-w-prose-p-5 :xl:max-w-full
              :overflow-x-scroll :xl:overflow-x-clip
              :overflow-y-clip
              :-mx-5 :xl:mx-0])
    [:table (tw [:border-separate :border-spacing-x-5 :-mx-3
                 :table-fixed])
     [:thead
      [wide:TopRow steps]]
     [:tbody
      [wide:ValueRow steps :frontend.views.roadmap/net-cost-this-step
       [:rvm/individual-values :rvm/h-net-project-cost]]
      [wide:ValueRow steps :frontend.views.roadmap/ten-year-this-step
       [:rvm/individual-values :rvm/k-payback-factor]]
      [wide:ValueRow steps :frontend.views.roadmap/net-cost-this-plus-prev
       [:rvm/cumulative-values :rvm/h-net-project-cost]]
      [wide:ValueRow steps :frontend.views.roadmap/ten-year-this-plus-prev
       [:rvm/cumulative-values :rvm/k-payback-factor]]
      [wide:BottomRow steps]
      [wide:SelectRow steps]]]]
   [detail:Wrapper steps "ecm-detail-wide"]])

;; Narrow Roadmap

(defn narrow:StepBar
  [total-energy-max a-total-energy-use step-color]
  (let [total-energy-step (qty/get-magnitude a-total-energy-use)
        bar-width (str (int (* 100 (double (/ total-energy-step total-energy-max)))) "%")]
    [:div (tw [:col-span-4]
              {:style {}
               :aria-label (str "Bar showing " bar-width " of baseline total energy use")})
     [:div (tw [:h-5 :print:h-3]
               {:style {:background (color/single step-color 1)
                        :width bar-width}})]]))

(defn narrow:StepHeader
  [title total-energy-use]
  [:<>
   [:div (tw [:font-bold :text-xl :col-span-3 :print:text-sm]) title]
   [:div (tw [:text-right :align-baseline])
    [qty/Mag (tw [:text-xl :print:text-sm]) total-energy-use]
    [qty/Unit (tw [:text-lg :print:text-sm :text-neutral-4 :font-thin :pl-1])
     total-energy-use]]])

(defn narrow:StepValue
  [title-key value]
  (if value
    [:div (tw [:text-left :pr-2])
     [:span (tw [:block :text-neutral-4 :font-thin :leading-4])
      [Tr title-key]]
     [:span (tw [:block]) [MaybeCurrency value]]]
    [:div]))

(defn narrow:Step
  [{:keys [:rvm/cumulative-values
           :rvm/individual-values
           :rvm/step-color
           :rvm/step-key
           :rvm/step-title
           :rvm/total-energy-max]
    :as step}]
  (let [{:keys [:rvm/a-total-energy-use]} cumulative-values
        on-click (when (should-enable-click? step)
                   (fn [_]
                     (rf/dispatch [:frontend.views.roadmap/set-selected-step
                                   step-key])
                     (js/setTimeout (partial detail:scroll-to-details "ecm-detail-narrow") 200)))]
    [:<>
     [:a (tw [:block :relative :col-span-4 :group]
             {:style {:height "7.8rem"}
              :on-click on-click})]
     [narrow:StepBar total-energy-max a-total-energy-use step-color]
     [narrow:StepHeader step-title a-total-energy-use]
     [narrow:StepValue :frontend.views.roadmap/net-cost-this-step
      (:rvm/h-net-project-cost individual-values)]
     [narrow:StepValue :frontend.views.roadmap/ten-year-this-step
      (:rvm/k-payback-factor individual-values)]
     [narrow:StepValue :frontend.views.roadmap/net-cost-this-plus-prev
      (:rvm/h-net-project-cost cumulative-values)]
     [narrow:StepValue :frontend.views.roadmap/ten-year-this-plus-prev
      (:rvm/k-payback-factor cumulative-values)]
     ;; This div is a placeholder idea for something to communicate that the ECMs
     ;; are clickable
     #_[:div (tw [:text-3xl :self-center :justify-self-center :text-neutral-4]) "ⓘ"]
     [:div (tw [:col-span-4])]]))

(defn narrow:Roadmap
  "Displays Roadmap as a horizontal bar chart with additional values between
  each row."
  [steps]
  [:div (tw [:px-5 :print:px-0])
   [:div (tw [:grid :w-full :max-w-full]
             {:style {:grid-template-columns "repeat(4, auto)"
                      :grid-auto-rows "1rem auto auto auto 1rem"
                      :print-color-adjust "exact"
                      :color-adjust "exact"
                      "WebkitPrintColorAdjust" "exact"}})
    (for [step steps]
      ^{:key (:rvm/step-key step)}
      [narrow:Step step])]
   [:div (tw [:mt-10 :print:hidden])
    [detail:Wrapper steps "ecm-detail-narrow"]]
   ;; for now only print portrait mode
   [:div (tw [:hidden :print:block])
    [print:Wrapper steps]]])

(defn add-selection-to-view-model
  [view-model selected-step-key]
  (map (fn add-selection-to-step
         [{:keys [:rvm/step-key] :as step}]
         (cond
           (nil? selected-step-key)          (assoc step :rvm/selected :default)
           (not= step-key selected-step-key) (assoc step :rvm/selected :unselected)
           (=    step-key selected-step-key) (assoc step :rvm/selected :selected)))
       view-model))

(defn Roadmap
  [{:keys [:roadmap/width :roadmap/view-model]}]
  (let [*selected-step-key (rf/subscribe [:frontend.views.roadmap/selected-step])
        steps (add-selection-to-view-model view-model @*selected-step-key)]
    (case width
      :wide   [wide:Roadmap steps]
      :narrow [narrow:Roadmap steps]
      nil)))

(defmethod graft/scion (u/->scion-id :frontend.views.roadmap/Roadmap)
  [roadmap-ctx container]
  (let [root (rg-dom-client/create-root container)
        first-ecm-key (get-in roadmap-ctx [:roadmap/view-model 1 :rvm/step-key])]
    (rf/dispatch [:frontend.views.roadmap/set-selected-step first-ecm-key])
    (rg-dom-client/render root [Roadmap roadmap-ctx])))
